import { ref, Ref } from '@nuxtjs/composition-api';
import {
  CheckoutPrice,
  PayPalInstance,
  RecurlyError,
  SubscriptionPricingState,
  TokenPayload,
} from '@recurly/recurly-js';
import { RecurlyPlanCode } from '~/types/plan';

type PricingChange = (plan: CheckoutPrice) => void;

export interface PlanInfo {
  code: RecurlyPlanCode;
  name: string;
  price: {
    unit_amount: number;
    symbol: string;
  };
  period?: {
    length: number;
  };
}

interface UseRecurly {
  recurlyError: Ref<RecurlyError | null>;
  paypalInstance: Ref<PayPalInstance | null>;
  isInitialising: Ref<boolean>;
  currentPricing: Ref<CheckoutPrice | null>;
  init(cardEl: HTMLElement, pricingCheckoutEl: HTMLElement, onPricingChange?: PricingChange): void;
  getCCToken(upgradeForm: HTMLFormElement): Promise<TokenPayload>;
  getPlanInfoFromRecurly(planCode: string, currency: string): Promise<PlanInfo>;
  startPayPalCheckout(): void;
  initPayPal(): void;
}

const isSubscriptionPricingState = (plan: any): plan is SubscriptionPricingState => {
  return plan.price !== undefined;
};

export const useRecurly = (): UseRecurly => {
  const error = ref<RecurlyError | null>(null);
  const isInitialising = ref(true);
  const paypalInstance = ref<PayPalInstance | null>(null);
  const currentPricing = ref<CheckoutPrice | null>(null);

  const onError = (e: RecurlyError): void => {
    console.error(e);
    error.value = e;
  };

  const init = (
    cardEl: HTMLElement,
    pricingCheckoutEl: HTMLElement,
    onPricingChange?: PricingChange
  ): void => {
    if (!window.recurly) {
      isInitialising.value = false;
      throw new Error('Recurly not loaded');
    }
    paypalInstance.value = window.recurly.PayPal({
      display: {
        displayName: 'TeachStarter',
      },
    });
    const elements = window.recurly.Elements();
    const cardElement = elements.CardElement();
    const checkoutPricing = window.recurly.Pricing.Checkout();

    checkoutPricing.on('change', (pricing: CheckoutPrice) => {
      currentPricing.value = pricing;
      if (onPricingChange) {
        onPricingChange(pricing);
      }
    });
    // @ts-ignore
    checkoutPricing.on('error', onError);
    paypalInstance.value.on('error', onError);

    cardElement.attach(cardEl);
    checkoutPricing.attach(pricingCheckoutEl);
    isInitialising.value = false;
  };
  const initPayPal = (): void => {
    paypalInstance.value = window.recurly.PayPal({
      display: {
        displayName: 'TeachStarter',
      },
    });
  };
  const startPayPalCheckout = (): void => {
    if (!paypalInstance.value) {
      throw new Error('PayPal instance not initialised');
    }

    paypalInstance.value.start();
  };

  const getCCToken = (upgradeForm: HTMLFormElement): Promise<TokenPayload> => {
    return new Promise<TokenPayload>((resolve, reject) => {
      window.recurly.token(upgradeForm, (err, token) => (err ? reject(err) : resolve(token)));
    });
  };

  const getPlanInfoFromRecurly = (planCode: string, currency: string): Promise<PlanInfo> => {
    if (!window.recurly) {
      throw new Error('Recurly not loaded');
    }

    // Creating a promise here as the recurly.js API is callback based
    return new Promise((resolve, reject) => {
      // We don't need to go to recurly for free plans.
      if (planCode === 'free') {
        resolve({
          code: 'free',
          name: 'Free',
          price: {
            symbol: '$',
            unit_amount: 0,
          },
        });
        return;
      }

      // Annoyingly we need to assign plan here, but wait for the done() callback
      // to be called otherwise it won't have any information. There's a lot of mutations
      // happening inside the recurly.js library so it has been somwewhat unpredicatble and
      // hard to debug.
      const plan = window.recurly.Pricing.Subscription()
        .plan(planCode)
        .catch(reject)
        .done(() => {
          if (!isSubscriptionPricingState(plan)) {
            reject(new Error('Pricing state is invalid. Please reload and try again'));
            return;
          }
          // @ts-ignore. Their types are wrong, doesn't match what is outputted.
          const planItem = plan.items.plan;
          if (planItem) {
            const planPrice = planItem.price[currency?.toUpperCase()];
            if (planPrice) {
              // Get the price associated with the users currency
              resolve({ ...planItem, price: { ...planPrice } });
            } else {
              reject(
                new Error(
                  `No price for ${currency} for plan ${planCode}. Available currencies are ${Object.keys(
                    planItem.price
                  )}`
                )
              );
            }
          } else {
            reject(new Error(`Plan ${planCode} not found`));
          }
        });
    });
  };

  return {
    recurlyError: error,
    isInitialising,
    paypalInstance,
    init,
    getCCToken,
    getPlanInfoFromRecurly,
    startPayPalCheckout,
    currentPricing,
    initPayPal,
  };
};
